import { isPowerOfTwo } from "./../../math/math.ts";
import { ArrayMultimap } from "../../utils/array-multimap.js";
import { getBasename } from "../../utils/file.ts";
import { createAtlas, TextureAtlas } from "./atlas.js";

/**
 * a basic texture cache object
 * @ignore
 */
class TextureCache {
	/**
	 * @ignore
	 */
	constructor(renderer, max_size = Infinity) {
		// reference to the renderer that owns this cache
		this.renderer = renderer;
		// cache uses an array to allow for duplicated key
		this.cache = new ArrayMultimap();
		this.tinted = new Map();
		this.units = new Map();
		this.usedUnits = new Set();
		this.max_size = max_size;
		this.clear();
	}

	/**
	 * @ignore
	 */
	clear() {
		this.cache.clear();
		this.tinted.clear();
		this.units.clear();
		this.usedUnits.clear();
	}

	/**
	 * @ignore
	 */
	allocateTextureUnit() {
		// find the first unit available among the max_size
		for (let unit = 0; unit < this.max_size; unit++) {
			// Check if unit is available
			if (!this.usedUnits.has(unit)) {
				// Add to used set
				this.usedUnits.add(unit);
				// return the new unit
				return unit;
			}
		}

		// No units available — flush the current batch and reset assignments
		// see https://github.com/melonjs/melonJS/issues/1280
		if (this.renderer.currentBatcher) {
			this.renderer.currentBatcher.flush();
			this.renderer.currentBatcher.boundTextures.length = 0;
			this.renderer.currentBatcher.currentTextureUnit = -1;
		}
		this.units.clear();
		this.usedUnits.clear();
		this.usedUnits.add(0);
		return 0;
	}

	/**
	 * Reset all texture unit assignments without clearing the texture cache.
	 * Used by multi-texture batching when the shader's sampler range is exceeded.
	 * @ignore
	 */
	resetUnitAssignments() {
		if (this.renderer.currentBatcher) {
			this.renderer.currentBatcher.boundTextures.length = 0;
			this.renderer.currentBatcher.currentTextureUnit = -1;
		}
		this.units.clear();
		this.usedUnits.clear();
	}

	/**
	 * @ignore
	 */
	freeTextureUnit(texture) {
		const source = texture.sources.get(texture.activeAtlas);
		const unit = this.units.get(source);
		// was a texture unit allocated ?
		if (typeof unit !== "undefined") {
			this.usedUnits.delete(unit);
			this.units.delete(source);
		}
	}

	/**
	 * @ignore
	 */
	getUnit(texture) {
		const source = texture.sources.get(texture.activeAtlas);
		if (!this.units.has(source)) {
			this.units.set(source, this.allocateTextureUnit());
		}
		return this.units.get(source);
	}

	/**
	 * @ignore
	 * return the texture unit for the given texture, or -1 if not allocated
	 */
	peekUnit(texture) {
		const source = texture.sources.get(texture.activeAtlas);
		return this.units.has(source) ? this.units.get(source) : -1;
	}

	/**
	 * @ignore
	 * cache the textureAltas for the given image
	 */
	set(image, textureAtlas) {
		const width = image.width || image.videoWidth;
		const height = image.height || image.videoHeight;

		// warn if a non POT texture is added to the cache when using WebGL1
		if (
			this.renderer.WebGLVersion === 1 &&
			(!isPowerOfTwo(width) || !isPowerOfTwo(height))
		) {
			const src = typeof image.src !== "undefined" ? image.src : image;
			console.warn(
				"[Texture] " +
					src +
					" is not a POT texture " +
					"(" +
					width +
					"x" +
					height +
					")",
			);
		}
		return this.cache.put(image, textureAtlas);
	}

	/**
	 * @ignore
	 */
	has(image) {
		return this.cache.has(image);
	}

	/**
	 * @ignore
	 * return the textureAltas for the given image
	 */
	get(image, atlas) {
		let entry = this.cache.get(image)[0];

		if (typeof entry !== "undefined" && typeof atlas !== "undefined") {
			this.cache.forEach((value, key) => {
				const _atlas = value.getAtlas();
				if (
					key === image &&
					_atlas.width === atlas.framewidth &&
					_atlas.height === atlas.frameheight
				) {
					entry = value;
				}
			});
		}

		if (typeof entry === "undefined") {
			if (!atlas) {
				atlas = createAtlas(
					image.width || image.videoWidth,
					image.height || image.videoHeight,
					image.src ? getBasename(image.src) : undefined,
				);
			}
			entry = new TextureAtlas(atlas, image, false);
			this.set(image, entry);
		}

		// "activate" the corresponding sources (in case of multi texture atlas)
		if (typeof entry.sources !== "undefined" && entry.sources.size > 1) {
			// manage cases where a specific atlas is specified
			for (const [key, value] of entry.sources.entries()) {
				// Check if the imageData matches the provided image
				if (value === image) {
					entry.activeAtlas = key;
				}
			}
		}

		return entry;
	}

	/**
	 * @ignore
	 */
	delete(image) {
		if (this.cache.has(image)) {
			const texture = this.cache.get(image)[0];
			if (typeof texture !== "undefined") {
				this.freeTextureUnit(texture);
			}
			this.cache.delete(image);
		}
	}

	/**
	 * @ignore
	 */
	tint(src, color) {
		// make sure the src is in the cache
		let image_cache = this.tinted.get(src);

		if (image_cache === undefined) {
			image_cache = new Map();
			this.tinted.set(src, image_cache);
		}

		if (!image_cache.has(color)) {
			image_cache.set(color, this.renderer.tint(src, color, "multiply"));
		}

		return image_cache.get(color);
	}
}
export default TextureCache;
